// 原生回调管理器
// 用于管理iOS和Android的原生回调

import { logout } from './auth';
import { notifyNativeDriverLogout } from './nativeBridge';
import { Toast } from 'antd-mobile';

// 派送成功回调参数类型
export interface DeliverySuccessParams {
    trackingNumber?: string;
    deliveryTaskOrderId?: number;
    fromMapMode?: boolean;
    detailType?: string;
}

export type NativeCallbackHandler = (params?: any) => void;

export class NativeCallbackManager {
    // 导航-派送成功
    static readonly DELIVERY_SUCCESS = 'deliverySuccess' as const;
    // 导航-派送失败
    static readonly DELIVERY_FAILED = 'deliveryFailed' as const;
    // 通用web跳转
    static readonly WEB_NAVIGATE = 'webNavigate' as const;

    private callbacks: Map<string, NativeCallbackHandler[]> = new Map();

    constructor() {
        this.setupGlobalCallbacks();
    }

    // 注册回调
    on(type: string, handler: NativeCallbackHandler): void {
        if (!this.callbacks.has(type)) {
            this.callbacks.set(type, []);
        }
        this.callbacks.get(type)!.push(handler);
    }

    // 取消注册回调
    off(type: string, handler: NativeCallbackHandler): void {
        const handlers = this.callbacks.get(type);
        if (handlers) {
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    // 触发回调
    private trigger(type: string, params?: any): void {
        const handlers = this.callbacks.get(type);
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(params);
                } catch (error) {
                    console.error(`执行${type}回调失败:`, error);
                }
            });
        }
    }

    // 设置全局回调函数
    private setupGlobalCallbacks(): void {
        // iOS回调函数
        (window as any).onDeliverySuccessCallback = (params?: DeliverySuccessParams) => {
            this.trigger(NativeCallbackManager.DELIVERY_SUCCESS, params);
        };

        (window as any).onDeliveryFailedCallback = (params?: any) => {
            this.trigger(NativeCallbackManager.DELIVERY_FAILED, params);
        };

        (window as any).onWebNavigateCallback = async (params?: any) => {
            console.log('onWebNavigateCallback');
            const { pageName = '' } = params || {};
            if (pageName === '/delivery/logout') {
                if (window.location.pathname === '/login') {
                    notifyNativeDriverLogout('');
                    return;
                }
                await logout('/login', 'native');
            } else {
                this.trigger(NativeCallbackManager.WEB_NAVIGATE, params);
            }
        };

        // Android回调函数
        if (window?.AndroidApp) {
            window.AndroidApp.callDeliverySuccessCallback = (params?: DeliverySuccessParams) => {
                this.trigger(NativeCallbackManager.DELIVERY_SUCCESS, params);
            };

            window.AndroidApp.callDeliveryFailedCallback = (params?: any) => {
                this.trigger(NativeCallbackManager.DELIVERY_FAILED, params);
            };

            window.AndroidApp.callWebNavigateCallback = async (params?: any) => {
                console.log('onWebNavigateCallback');
                const { pageName = '' } = params || {};
                if (pageName === '/delivery/logout') {
                    if (window.location.pathname === '/login') {
                        notifyNativeDriverLogout('');
                        return;
                    }
                    await logout('/login', 'native');
                } else {
                    this.trigger(NativeCallbackManager.WEB_NAVIGATE, params);
                }
            };
        }
    }

    // 清理所有回调
    cleanup(): void {
        this.callbacks.clear();
    }
}

// 创建并导出全局实例
export const nativeCallbacks = new NativeCallbackManager();
